import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';

import { CmsArticle } from '@fcom/core-api/interfaces';
import { finShare } from '@fcom/rx';
import { ButtonMode, ButtonTheme, IconPosition, NotificationTheme } from '@fcom/ui-components';
import { compareAsJson, groupBy, toLookup, unique, unsubscribe } from '@fcom/core/utils';
import { ElementActions, ElementTypes, FragmentService, GaContext, GtmEvent, GtmPurchaseFlow } from '@fcom/common';
import { GtmService } from '@fcom/common/gtm';
import { getGA4ProductsFromSmpProducts } from '@fcom/common/utils/gtm.utils';
import {
  FinnairPassengerServiceSelectionItemWithStatus,
  FinnairServiceItemWithUpsell,
  FlightServices,
  RemovedServiceInfo,
  RemovedServiceProgress,
  SelectedServiceData,
} from '@fcom/dapi/interfaces';
import {
  Category,
  FinnairBoundItem,
  FinnairCart,
  FinnairOrder,
  FinnairPassengerCode,
  FinnairPassengerItem,
  FinnairPassengerServiceItem,
  FinnairPassengerServiceSelectionItem,
  FinnairServiceItem,
  FinnairServiceSegmentItem,
} from '@fcom/dapi/api/models';

import { SmpProduct } from '../product-list/product-list.component';
import { getGroupedServices, isFlight } from '../../../../utils/common-booking.utils';
import { getPassengerTierLevel, isBoundBasedCategory } from '../../utils';

@Component({
  selector: 'fin-dapi-included-service',
  styleUrls: ['./included-service.component.scss'],
  templateUrl: './included-service.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class IncludedServiceComponent implements OnDestroy, OnInit, OnChanges {
  readonly Category = Category;
  readonly FinnairPassengerCode = FinnairPassengerCode;
  readonly NotificationTheme = NotificationTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonMode = ButtonMode;
  readonly IconPosition = IconPosition;

  private subscriptions: Subscription = new Subscription();

  @Input() service: FinnairServiceItemWithUpsell;
  @Input() showAsPending = false;
  @Input() cartOrOrder$: Observable<FinnairCart | FinnairOrder>;
  @Input() passengers$: Observable<FinnairPassengerItem[]> = of([]);
  @Input() showUpsellPerPax: boolean = false;
  @Input() showUpsellPaymentButton = true;
  @Input() includeSeatActionButtons = false;
  @Input() showClearPendingSeatsButton = false;
  @Input() products$: Observable<SmpProduct[]> = new BehaviorSubject([]);
  @Input() gtmFlow: GtmPurchaseFlow = GtmPurchaseFlow.MANAGE_BOOKING;
  @Input() gaContext: GaContext = GaContext.MANAGE_BOOKING;
  @Input() isCarrierConnect = false;
  @Input() includeAssignAtAirportStatus = false;
  @Input() showNonAYSeats = false;
  @Input() removeProgressInfo$: Observable<RemovedServiceProgress> = of({ removeInProgress: false });
  @Input() isShoppingCart = false;
  @Input() groupServicesByBound = false;

  @Output() selectProduct: EventEmitter<SelectedServiceData> = new EventEmitter<SelectedServiceData>();
  @Output() continuePurchase: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() removeProduct: EventEmitter<RemovedServiceInfo> = new EventEmitter<RemovedServiceInfo>();
  @Output() viewSeatmap: EventEmitter<void> = new EventEmitter();
  @Output() clearPendingSeats: EventEmitter<void> = new EventEmitter();

  serviceSegmentItems$: Observable<FlightServices[]>;
  serviceSubject$: BehaviorSubject<FinnairServiceItem> = new BehaviorSubject<FinnairServiceItem>(null);
  hasNonAySegments$: Observable<boolean>;
  safProductInfo$: Observable<CmsArticle>;
  safThankYouForJoiningUsNotification$: Observable<CmsArticle>;
  isModalOpened = false;

  constructor(
    private gtmService: GtmService,
    private fragmentService: FragmentService
  ) {}

  ngOnInit(): void {
    this.serviceSubject$.next(this.service);
    this.serviceSegmentItems$ = this.findServiceInAllFlights();
    this.hasNonAySegments$ = this.serviceSegmentItems$.pipe(
      map((segments) => segments.some((seg) => seg.operatingAirline.code !== 'AY'))
    );

    this.safProductInfo$ = this.serviceSubject$.pipe(
      filter((service) => service.category === Category.SAF),
      switchMap(() => this.fragmentService.getFragment<CmsArticle>('fragments.services.productInfo.saf.contributeUrl'))
    );
    this.safThankYouForJoiningUsNotification$ = this.serviceSubject$.pipe(
      filter((service) => service.category === Category.SAF),
      switchMap(() => this.fragmentService.getFragment<CmsArticle>('fragments.thankYouForJoiningUs.url'))
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.service && !changes.service.isFirstChange()) {
      this.serviceSubject$.next(this.service);
    }
  }

  private findServiceInAllFlights(): Observable<FlightServices[]> {
    const isPassengerSelected = (
      selectedPassengers: FinnairPassengerItem[],
      passengerServiceItem: FinnairPassengerServiceItem
    ): boolean =>
      selectedPassengers.length === 0 || selectedPassengers.some(({ id }) => id === passengerServiceItem.id);

    return combineLatest([
      this.cartOrOrder$,
      combineLatest([this.passengers$, this.serviceSubject$]).pipe(
        map(([passengers, service]) =>
          service.category === Category.SEAT
            ? passengers.filter((pax) => pax.passengerTypeCode !== FinnairPassengerCode.INF)
            : passengers
        )
      ),
      this.serviceSubject$,
      this.removeProgressInfo$,
    ]).pipe(
      map(([order, selectedPassengers, service, removeInProgressInfo]) => {
        const { bounds, serviceCatalog } = order;
        const variantsWeCanSell =
          serviceCatalog?.categories
            ?.flatMap((serviceCategory) =>
              Object.values(serviceCategory.services).flatMap((servicesForFragment) =>
                Object.values(servicesForFragment).flatMap((passengerServices) =>
                  passengerServices.flatMap((passengerService) => passengerService.variant)
                )
              )
            )
            .filter(unique) ?? [];
        const allFlights = bounds.flatMap((bound) => bound.itinerary).filter(isFlight);
        const flightById = toLookup(allFlights, (flight) => flight.id);

        const serviceCatalogPerCategory = serviceCatalog?.categories.find(
          ({ category }) =>
            category === this.service.category &&
            [
              Category.CABIN_BAGGAGE,
              Category.BAGGAGE,
              Category.SEAT,
              Category.MEAL,
              Category.WIFI,
              Category.SPORT,
              Category.LOUNGE,
              Category.PET,
              Category.SAF,
              Category.COVER,
            ].includes(category)
        );

        const allServiceSegments: FinnairServiceSegmentItem[] = service.bounds.flatMap(({ segments }) => segments);
        const serviceSegmentsByFlightId = groupBy(allServiceSegments, (segment) => segment.id);

        const serviceBySegments = Object.entries(serviceSegmentsByFlightId)
          .map(([flightId, serviceSegmentsForFlight]) => {
            const passengerServices = serviceSegmentsForFlight
              .flatMap(({ passengers }) => passengers)
              .filter((passenger) => isPassengerSelected(selectedPassengers, passenger));

            const passengerIncludedServices = passengerServices.map((passenger) =>
              passenger.services.filter((s) => s.includedInTicketType)
            );

            const complimentaryServices =
              service.category === Category.MEAL
                ? passengerIncludedServices.reduce((shared, services) => {
                    return shared.filter((value) => services.some((service) => service.variant === value.variant));
                  })
                : [];

            const variantsToRemove = complimentaryServices.map((service) => service.variant);

            const flightPassengers = passengerServices
              .map((passenger) => {
                const boundId = Object.keys(serviceCatalogPerCategory?.lowestPrice || {}).find((boundId) =>
                  boundId.includes(flightId)
                );

                const isAvailableForSellServices =
                  boundId && serviceCatalogPerCategory?.lowestPrice[boundId][passenger.id] && this.showUpsellPerPax;

                const showedServices =
                  service.category === Category.MEAL
                    ? passenger.services.filter((service) => !variantsToRemove.includes(service.variant))
                    : passenger.services;

                return {
                  ...passenger,
                  passengerTypeCode: selectedPassengers.find((p) => p.id === passenger.id)?.passengerTypeCode,
                  groupedServices: getGroupedServices(
                    showedServices,
                    getPassengerTierLevel(passenger.id, selectedPassengers),
                    variantsWeCanSell,
                    isAvailableForSellServices
                      ? [
                          {
                            id: '',
                            subCategory: `${service.category}`,
                            totalPrice: serviceCatalogPerCategory?.lowestPrice[boundId][passenger.id].money,
                          } as unknown as FinnairPassengerServiceSelectionItem,
                        ]
                      : [],
                    this.isCarrierConnect,
                    this.includeAssignAtAirportStatus
                  ),
                };
              })
              .filter((passenger) => passenger.groupedServices.length > 0);

            const serviceSegmentItem = serviceSegmentsForFlight[serviceSegmentsForFlight.length - 1];
            const flight = flightById[flightId];

            return {
              ...serviceSegmentItem,
              operatingAirline: flight.operatingAirline,
              passengers: flightPassengers,
              complimentaryServices,
            } as FlightServices;
          })
          .filter((s) => s.passengers?.length > 0 || s.complimentaryServices.length > 0);
        const flightServices =
          this.groupServicesByBound && isBoundBasedCategory(this.service.category)
            ? this.mergeServicesForBound(serviceBySegments, bounds)
            : serviceBySegments;
        return this.updateRemovalStatus(flightServices, removeInProgressInfo);
      }),
      finShare()
    );
  }

  private updateRemovalStatus(flightServices: FlightServices[], removedServiceInfo: RemovedServiceProgress) {
    return flightServices.map((flightService) => {
      return {
        ...flightService,
        passengers: flightService.passengers.map((pax) => {
          return {
            ...pax,
            groupedServices: pax.groupedServices.map((groupedService) => {
              return {
                ...groupedService,
                services: groupedService.services.map((service) => {
                  return {
                    ...service,
                    removeInProgress: this.isRemoveInProgressForService(
                      removedServiceInfo,
                      flightService,
                      pax.id,
                      service
                    ),
                  };
                }),
              };
            }),
          };
        }),
      };
    });
  }

  private isRemoveInProgressForService(
    removedServiceInfo: RemovedServiceProgress,
    flightServices: FlightServices,
    paxId: string,
    service: FinnairPassengerServiceSelectionItemWithStatus
  ) {
    return (
      removedServiceInfo.removeInProgress &&
      removedServiceInfo.service.category === this.service.category &&
      paxId === removedServiceInfo.service.travelerId &&
      (flightServices.id === removedServiceInfo.service.fragmentId ||
        flightServices.id === removedServiceInfo.service.boundId) &&
      (this.service.category === Category.SEAT
        ? service.seatNumber === removedServiceInfo.service.seatNumber
        : service.variant === removedServiceInfo.service.variant)
    );
  }

  private mergeServicesForBound(flightServices: FlightServices[], bounds: FinnairBoundItem[]): FlightServices[] {
    return bounds
      .map((bound) => {
        const flightIds = bound.itinerary.filter(isFlight).map((f) => f.id);
        const allFlightServicesInBound = flightServices.filter((flightService) => flightIds.includes(flightService.id));
        if (allFlightServicesInBound.length === 0) {
          return null;
        }
        const servicesGroupedByBound = [
          {
            ...allFlightServicesInBound[0],
            departure: bound.departure,
            arrival: bound.arrival,
            id: bound.id,
          },
        ];
        return this.isSegmentServicesIdentical(allFlightServicesInBound)
          ? servicesGroupedByBound
          : allFlightServicesInBound;
      })
      .filter((bound) => !!bound)
      .reduce((allServices, currentServices) => {
        return [...allServices, ...currentServices];
      }, []);
  }

  private isSegmentServicesIdentical(flightServices: FlightServices[]): boolean {
    return flightServices.every((flightService) =>
      compareAsJson(flightService.passengers, flightServices[0].passengers)
    );
  }

  onContinuePurchase(): void {
    this.gtmService.trackElement(
      'continue-to-review_view-cta',
      this.gaContext,
      ElementTypes.BUTTON,
      undefined,
      ElementActions.CLICK
    );
    this.continuePurchase.emit(true);
  }

  onRemoveProduct(productInfo: RemovedServiceInfo, paxId: string, segmentId: string): void {
    this.removeProduct.emit({
      ...productInfo,
      travelerId: paxId,
      fragmentId: segmentId,
    });
  }

  onSelectProduct(category: Category, fragmentId: string, paxId: string): void {
    this.selectProduct.emit({
      category,
      fragmentId,
      paxId,
    });

    this.subscriptions.add(
      combineLatest([this.products$, this.cartOrOrder$])
        .pipe(
          take(1),
          filter(([products]) => products.length > 0)
        )
        .subscribe(([products, cartOrOrder]) => {
          const matchedProduct = products.find((product) => product.category === category);

          this.gtmService.ecommerceEventGA4(
            GtmEvent.SELECT_ITEM,
            getGA4ProductsFromSmpProducts([matchedProduct], this.gtmFlow, cartOrOrder.bounds)
          );
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }
}
